"use client"

import {
  Clock3,
  History,
} from "lucide-react"
import {
  AnimatePresence,
  motion,
} from "framer-motion"
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  useMusicContext,
} from "@/context/musicContext"

import RecentActivityItemRow from "./recent-activity-item"

import {
  MAX_ITEMS,
  formatRelativeTime,
  getSongId,
  readRecentActivity,
  saveRecentActivity,
  type RecentActivityItem,
} from "./recent-activity.types"

type Props = {
  limit?: number
  className?: string
}

export default function RecentActivity({
  limit = 6,
  className = "",
}: Props) {
  const {
    playSong,
    playlist,
    currentSong,
    currentPlaylist,
    isPlaying,
    currentTrackData,
  } = useMusicContext()

  const [items, setItems] = useState<
    RecentActivityItem[]
  >([])

  const [now, setNow] = useState(
    Date.now(),
  )

  /* =========================================================
     LOAD
  ========================================================= */

  useEffect(() => {
    setItems(readRecentActivity())
  }, [])

  /* =========================================================
     RELATIVE TIME
  ========================================================= */

  useEffect(() => {
    const interval =
      window.setInterval(
        () => {
          setNow(Date.now())
        },
        60_000,
      )

    return () => {
      window.clearInterval(
        interval,
      )
    }
  }, [])

  /* =========================================================
     REGISTER CURRENT SONG
  ========================================================= */

  useEffect(() => {
    if (!currentSong) {
      return
    }

    /*
     * Las radios no entran en este historial.
     */
    if (
      currentTrackData?.type ===
      "radio"
    ) {
      return
    }

    if (!currentPlaylist) {
      return
    }

    const index =
      typeof currentTrackData
        ?.trackKey?.[1] === "number"
        ? currentTrackData.trackKey[1]
        : 0

    const id = getSongId(
      currentSong,
      currentPlaylist,
      index,
    )

    const item: RecentActivityItem = {
      id,

      type: "song",

      title:
        currentSong.songName ||
        "Sin título",

      artist:
        currentSong.songArtist ||
        "Artista desconocido",

      image:
        currentSong.songimg,

      playlistId:
        currentPlaylist.id,

      playlistTitle:
        currentPlaylist.title,

      playlistLink:
        currentPlaylist.link,

      songIndex: index,

      playedAt: Date.now(),
    }

    setItems((previous) => {
      const withoutCurrent =
        previous.filter(
          (existing) =>
            existing.id !== id,
        )

      const next = [
        item,
        ...withoutCurrent,
      ].slice(0, MAX_ITEMS)

      saveRecentActivity(next)

      return next
    })
  }, [
    currentSong,
    currentPlaylist,
    currentTrackData,
  ])

  /* =========================================================
     CURRENT ID
  ========================================================= */

  const currentId = useMemo(() => {
    if (
      !currentSong ||
      !currentPlaylist
    ) {
      return null
    }

    const index =
      typeof currentTrackData
        ?.trackKey?.[1] === "number"
        ? currentTrackData.trackKey[1]
        : 0

    return getSongId(
      currentSong,
      currentPlaylist,
      index,
    )
  }, [
    currentSong,
    currentPlaylist,
    currentTrackData,
  ])

  /* =========================================================
     VISIBLE ITEMS
  ========================================================= */

  const visibleItems = useMemo(
    () =>
      items.slice(0, limit),
    [items, limit, now],
  )

  /* =========================================================
     PLAY FROM HISTORY
  ========================================================= */

  const handlePlay = useCallback(
    (
      item: RecentActivityItem,
    ) => {
      if (item.type !== "song") {
        return
      }

      const targetPlaylist =
        playlist.find((p: any) => {
          if (
            item.playlistId !==
            undefined
          ) {
            return (
              p.id ===
              item.playlistId
            )
          }

          return (
            p.title ===
            item.playlistTitle
          )
        })

      if (!targetPlaylist) {
        console.warn(
          "[RecentActivity] Playlist no encontrada:",
          item.playlistTitle,
        )

        return
      }

      const songs =
        Array.isArray(
          targetPlaylist.playlistData,
        )
          ? targetPlaylist.playlistData
          : []

      if (!songs.length) {
        return
      }

      let index =
        typeof item.songIndex ===
        "number"
          ? item.songIndex
          : -1

      /*
       * El índice puede haber cambiado
       * si la playlist fue modificada.
       */

      if (
        index < 0 ||
        index >= songs.length ||
        songs[index]?.songName !==
          item.title
      ) {
        index = songs.findIndex(
          (song: any) =>
            song?.songName ===
              item.title &&
            song?.songArtist ===
              item.artist,
        )
      }

      if (index < 0) {
        console.warn(
          "[RecentActivity] Canción no encontrada:",
          item.title,
        )

        return
      }

      playSong(
        songs[index],
        index,
        targetPlaylist,
      )
    },
    [
      playlist,
      playSong,
    ],
  )

  /* =========================================================
     EMPTY STATE
  ========================================================= */

  if (!visibleItems.length) {
    return (
      <section
        className={`
          m-4
          relative
          overflow-hidden
          rounded-2xl
          border
          border-white/[.06]
          bg-[var(--soft-bg)]
          ${className}
        `}
      >
        <div className="px-4 py-4">
          <div className="flex items-center gap-2">
            <div
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-xl
                bg-white/[.035]
                ring-1
                ring-white/[.06]
              "
            >
              <History
                size={15}
                strokeWidth={1.7}
                className="text-white/35"
              />
            </div>

            <div className="min-w-0">
              <p
                className="
                  text-[9px]
                  font-medium
                  uppercase
                  tracking-[.18em]
                  text-white/55
                "
              >
                Actividad reciente
              </p>

              <p
                className="
                  mt-1
                  text-[10px]
                  text-white/25
                "
              >
                Tus últimas reproducciones
                aparecerán aquí.
              </p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <section
      className={`
        m-4
        relative
        overflow-hidden
        rounded-2xl
        border
        //border-white/[.06]
        border-[var(--primary-color)]/12
        //bg-[var(--soft-bg)]
        bg-white/7
        bg-[var(--color-sombra)]
        ${className}
      `}
    >
      {/* =====================================================
          SOFT AMBIENT LIGHT
      ===================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -right-20
          -top-20
          h-40
          w-40
          rounded-full
          bg-[var(--primary-color)]/[.025]
          blur-[65px]
        "
      />

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        className="
          relative
          flex
          items-center
          justify-between
          px-4
          pb-2
          pt-4
        "
      >
        <div className="flex items-center gap-2.5">
          <div
            className="
              flex
              h-7
              w-7
              items-center
              justify-center
              rounded-lg
              bg-white/[.035]
              ring-1
              ring-white/[.06]
            "
          >
            <Clock3
              size={13}
              strokeWidth={1.7}
              className="text-white/45"
            />
          </div>

          <div>
            <p
              className="
                text-[9px]
                font-medium
                uppercase
                tracking-[.18em]
                text-white/65
              "
            >
              Actividad reciente
            </p>

            <p
              className="
                mt-0.5
                text-[9px]
                text-white/25
              "
            >
              Últimas reproducciones
            </p>
          </div>
        </div>

        <span
          className="
            text-[8px]
            tabular-nums
            text-white/20
          "
        >
          {visibleItems.length}
        </span>
      </div>

      {/* =====================================================
          LIST
      ===================================================== */}

      <div className="relative px-2 pb-2">
        <AnimatePresence
          initial={false}
          mode="popLayout"
        >
          {visibleItems.map(
            (item, index) => {
              const isCurrent =
                item.id === currentId

              return (
                <RecentActivityItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  isCurrent={
                    isCurrent
                  }
                  isPlaying={
                    isPlaying
                  }
                  onPlay={
                    handlePlay
                  }
                  relativeTime={formatRelativeTime(
                    item.playedAt,
                  )}
                />
              )
            },
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}